import { Control, UseFormSetValue, useWatch } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CityConfigFormValues,
  EASYPARK_CODE_HELP,
  RIGHTS_PROVIDER_ARRAY_FIELDS,
  RIGHTS_PROVIDER_OPTIONS,
} from "../../parking-payment.const";
import { RightsProviderEnum, TicketProviderEnum } from "../../payment-zone-area.type";
import { EditableStringList } from "./editable-string-list";
import { SubSectionLabel } from "./sub-section-label";

type RightsProviderSectionProps = {
  control: Control<CityConfigFormValues>;
  setValue: UseFormSetValue<CityConfigFormValues>;
};

const RIGHTS_ARRAY_FIELDS = RIGHTS_PROVIDER_ARRAY_FIELDS.map((field) => `rightsProvider.${field}` as const);

export const RightsProviderSection = ({ control, setValue }: RightsProviderSectionProps) => {
  const provider = useWatch({ control, name: "rightsProvider.provider" });
  const ticketProviderType = useWatch({ control, name: "ticketProvider.provider" });
  const ticketCode = useWatch({ control, name: "ticketProvider.code" });

  const handleProviderChange = (value: string) => {
    const next = value === "__none__" ? undefined : (value as RightsProviderEnum);
    setValue("rightsProvider.provider", next);
    setValue("rightsProvider.code", "");
    RIGHTS_ARRAY_FIELDS.forEach((name) => setValue(name, []));
  };

  const canCopyFromTicketProvider = ticketProviderType === TicketProviderEnum.Easypark && !!ticketCode?.trim();

  const copyFromTicketProvider = () => {
    setValue("rightsProvider.code", ticketCode ?? "", { shouldValidate: true });
  };

  const isEasypark = provider === RightsProviderEnum.Easypark;
  const isExtenso = provider === RightsProviderEnum.Extenso;
  const isPolyconseil = provider === RightsProviderEnum.Polyconseil;
  const isPrestoPlatform = provider === RightsProviderEnum.PrestoPlatform;

  return (
    <div className="space-y-4">
      <FormField
        control={control}
        name="rightsProvider.provider"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Type de provider</FormLabel>
            <Select onValueChange={handleProviderChange} value={field.value ?? ""}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionnez un provider" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="__none__">Aucun</SelectItem>
                {RIGHTS_PROVIDER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      {provider && (
        <>
          {isEasypark && canCopyFromTicketProvider && (
            <Button type="button" variant="outline" size="sm" onClick={copyFromTicketProvider}>
              Copier depuis le concentrateur de tickets
            </Button>
          )}

          <FormField
            control={control}
            name="rightsProvider.code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Code <span className="text-red-500">*</span>
                </FormLabel>
                {isEasypark && <SubSectionLabel label={EASYPARK_CODE_HELP} />}
                <FormControl>
                  <Input placeholder={isEasypark ? "Ex. lisbon" : "Ex. BAG0028"} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {isExtenso && (
            <>
              <FormField
                control={control}
                name="rightsProvider.extensoProfiles"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormLabel>Profils</FormLabel>
                    <FormControl>
                      <EditableStringList value={value ?? []} onChange={onChange} placeholder="Ex. resident" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="rightsProvider.extensoValidityAreas"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormLabel>Zones de validité</FormLabel>
                    <FormControl>
                      <EditableStringList value={value ?? []} onChange={onChange} placeholder="Ex. Verte" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}

          {isPolyconseil && (
            <>
              <FormField
                control={control}
                name="rightsProvider.polyconseilSubscriptionPlans"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormLabel>Plans d'abonnement</FormLabel>
                    <FormControl>
                      <EditableStringList value={value ?? []} onChange={onChange} placeholder="Ex. Visiteurs" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="rightsProvider.polyconseilValidityAreas"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormLabel>Zones de validité</FormLabel>
                    <FormControl>
                      <EditableStringList value={value ?? []} onChange={onChange} placeholder="Ex. VA_centre" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}

          {isPrestoPlatform && (
            <FormField
              control={control}
              name="rightsProvider.prestoPlatformUrbanParkingSiteIds"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>
                    Identifiants de sites <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <EditableStringList value={value ?? []} onChange={onChange} placeholder="Ex. UPS-01" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </>
      )}
    </div>
  );
};
